import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useBackups, useWorkspaces } from '../hooks/useClaw9';
import { downloadBackup } from '../api/backups';
import BackupRestoreModal from './BackupRestoreModal';

/**
 * Panel displaying backup list with actions
 */
function BackupListPanel({ className = '' }) {
    const { backups, isLoading, error, createBackup, deleteBackup, refresh } = useBackups();
    const { workspaces } = useWorkspaces();

    const [showCreateForm, setShowCreateForm] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDownloading, setIsDownloading] = useState(null);
    const [restoreBackup, setRestoreBackup] = useState(null);
    const [actionError, setActionError] = useState(null);

    // Create form state
    const [newBackupWorkspace, setNewBackupWorkspace] = useState('');
    const [newBackupName, setNewBackupName] = useState('');

    const handleCreate = async (e) => {
        e.preventDefault();
        setIsCreating(true);
        setActionError(null);
        try {
            await createBackup({
                botId: newBackupWorkspace || undefined,
                metadata: newBackupName.trim() ? { name: newBackupName.trim() } : undefined
            });
            setNewBackupWorkspace('');
            setNewBackupName('');
            setShowCreateForm(false);
        } catch (err) {
            setActionError(err.message);
        } finally {
            setIsCreating(false);
        }
    };

    const handleDelete = async (backupId) => {
        setIsDeleting(true);
        setActionError(null);
        try {
            await deleteBackup(backupId);
            setDeleteConfirmId(null);
        } catch (err) {
            setActionError(err.message);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDownload = async (backup) => {
        setIsDownloading(backup.backup_id);
        setActionError(null);
        try {
            const result = await downloadBackup(backup.backup_id);
            const blob = result.data;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = backup.filename || `${backup.backup_id}.tar.gz`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            setActionError(err.message);
        } finally {
            setIsDownloading(null);
        }
    };


    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatSize = (bytes) => {
        if (!bytes) return 'N/A';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    if (isLoading && backups.length === 0) {
        return (
            <div className={`flex items-center justify-center h-64 ${className}`}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
        );
    }

    return (
        <div className={className}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-medium text-gray-900">Backups</h2>
                    <p className="text-sm text-gray-500">
                        {backups.length} backup{backups.length !== 1 ? 's' : ''} available
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={refresh}
                        disabled={isLoading}
                        className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                        Refresh
                    </button>
                    <button
                        onClick={() => setShowCreateForm(!showCreateForm)}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                    >
                        {showCreateForm ? 'Cancel' : 'Create Backup'}
                    </button>
                </div>
            </div>

            {/* Errors */}
            {(error || actionError) && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error || actionError}</p>
                </div>
            )}

            {/* Create Form */}
            {showCreateForm && (
                <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <form onSubmit={handleCreate} className="space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="backup-workspace" className="block text-sm font-medium text-gray-700">
                                    Workspace (Optional)
                                </label>
                                <select
                                    id="backup-workspace"
                                    value={newBackupWorkspace}
                                    onChange={(e) => setNewBackupWorkspace(e.target.value)}
                                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                    disabled={isCreating}
                                >
                                    <option value="">All workspaces</option>
                                    {workspaces.map(ws => (
                                        <option key={ws.bot_id} value={ws.bot_id}>{ws.bot_id}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="backup-name" className="block text-sm font-medium text-gray-700">
                                    Backup Name (Optional)
                                </label>
                                <input
                                    id="backup-name"
                                    type="text"
                                    value={newBackupName}
                                    onChange={(e) => setNewBackupName(e.target.value)}
                                    placeholder="Auto-generated if empty"
                                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                    disabled={isCreating}
                                />
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={isCreating}
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                            >
                                {isCreating ? 'Creating...' : 'Create Backup'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Backup List */}
            {backups.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No backups</h3>
                    <p className="mt-1 text-sm text-gray-500">Create your first backup to protect your data.</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Workspace
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Created
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Size
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {backups.map(backup => (
                                <tr key={backup.backup_id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                                                <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                                </svg>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" title={backup.filename}>
                                                    {backup.filename || backup.backup_id}
                                                </div>
                                                <div className="text-xs text-gray-500 font-mono">
                                                    {backup.backup_id}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {backup.bot_id || 'All'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatDate(backup.created_at)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatSize(backup.size_bytes)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            onClick={() => setRestoreBackup(backup)}
                                            className="text-indigo-600 hover:text-indigo-900 mr-3"
                                        >
                                            Restore
                                        </button>
                                        <button
                                            onClick={() => handleDownload(backup)}
                                            disabled={isDownloading === backup.backup_id}
                                            className="text-green-600 hover:text-green-900 mr-3 disabled:opacity-50"
                                        >
                                            {isDownloading === backup.backup_id ? 'Downloading...' : 'Download'}
                                        </button>
                                        {deleteConfirmId === backup.backup_id ? (
                                            <span className="inline-flex gap-2">
                                                <button
                                                    onClick={() => handleDelete(backup.backup_id)}
                                                    disabled={isDeleting}
                                                    className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                                >
                                                    {isDeleting ? 'Deleting...' : 'Confirm'}
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirmId(null)}
                                                    className="text-gray-600 hover:text-gray-900"
                                                >
                                                    Cancel
                                                </button>
                                            </span>
                                        ) : (
                                            <button
                                                onClick={() => setDeleteConfirmId(backup.backup_id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Restore Modal */}
            {restoreBackup && (
                <BackupRestoreModal
                    backup={restoreBackup}
                    workspaces={workspaces}
                    onClose={() => setRestoreBackup(null)}
                    onRestored={() => {
                        setRestoreBackup(null);
                        refresh();
                    }}
                />
            )}
        </div>
    );
}

BackupListPanel.propTypes = {
    className: PropTypes.string
};

export default BackupListPanel;
